import React, { useEffect } from 'react';
import { Display } from '../../components/Atoms';

/**
 * Modal — wspólny chrome dla wszystkich modali Plot CRUD.
 *
 * - Dark overlay + blur
 * - Klik w tło zamyka
 * - ESC zamyka
 * - Body lock scroll gdy otwarty
 * - aria-modal + role=dialog
 * - Header z X i tytułem
 */
export default function Modal({ open, onClose, title, subtitle, children, footer, maxWidth = 560 }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15, 20, 16, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line)',
          borderRadius: 16,
          maxWidth, width: '100%',
          maxHeight: '90dvh',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,.5)',
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: 14,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Display size={22} style={{ display: 'block', letterSpacing: '-.02em' }}>{title}</Display>
            {subtitle && (
              <div style={{
                fontSize: 13, color: 'var(--t-text-dim)',
                marginTop: 4, lineHeight: 1.4,
              }}>{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--t-text-mute)', cursor: 'pointer',
              fontSize: 24, lineHeight: 1, padding: 0,
              flexShrink: 0,
              width: 44, height: 44, margin: '-10px -10px 0 0',
              display: 'grid', placeItems: 'center',
            }}
          >×</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {children}
        </div>

        {footer && (
          <div style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--t-line)',
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            background: 'var(--t-bg-elev)',
          }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

// Spólne style inputów — używamy w polach modali
export const modalInputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  background: 'var(--t-bg-elev)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 16,
  letterSpacing: '-.01em',
  outline: 'none',
  boxSizing: 'border-box',
};

export function ModalLabel({ children, htmlFor, hint }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.22em', textTransform: 'uppercase',
          color: 'var(--t-text-dim)',
          marginBottom: hint ? 2 : 0,
        }}
      >{children}</label>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--t-text-mute)' }}>{hint}</div>
      )}
    </div>
  );
}

export function ModalField({ children, style }) {
  return (
    <div style={{ marginBottom: 18, ...style }}>{children}</div>
  );
}

export function ModalError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 10,
      background: 'rgba(192, 106, 74, 0.08)',
      borderLeft: '3px solid var(--t-rust)',
      fontSize: 13, color: 'var(--t-text)',
      marginBottom: 14,
    }}>{message}</div>
  );
}
